import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaStar, FaMapMarkerAlt, FaClock, FaTag, FaCalendarAlt, FaUserFriends, FaMapPin, FaBinoculars, FaUtensils, FaBed, FaCar, FaPlane, FaCheck, FaTimes, FaRegCalendarAlt, FaPlus, FaMinus } from "react-icons/fa";
import Navbar from "../../components/common/Navbar";
import Footer from "../../components/home/Footer";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import SafariMapSection from "../../components/home/SafariMapSection";
import { MapContainer, TileLayer, Marker, Popup, Polyline, ZoomControl, LayersControl, ScaleControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconShadowUrl from "leaflet/dist/images/marker-shadow.png";
import type { LatLngTuple, LatLngExpression } from 'leaflet';
import ButtonCTA from "../../components/ui/button-cta";
import { X, Phone, Calendar, Check, MapPin, Car, Bed, Users, Clock, CalendarDays, CalendarRange, Info, Waves, Map } from "lucide-react";
import TourHeader from "@/components/TourHeader";
import TripDetails from "@/components/TripDetails";
import ChecklistSection from "@/components/ChecklistSection";
import TabContent from "@/components/TabContent";
import GoogleMap from "@/components/GoogleMap";
import QASection from "@/components/QASection";
import TripGallery from "@/components/TripGallery";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import GuideCard from "@/components/GuideCard";
import { animationClasses } from "@/utils/animations";
import TourHero from "@/components/TourHero";

// Initialize Leaflet icons configuration once
const iconConfig = () => {
  // Delete default icon references
  delete (L.Icon.Default.prototype as any)._getIconUrl;

  // Configure default icons
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: "/images/marker-icon-2x.png",
    iconUrl: "/images/marker-icon.png",
    shadowUrl: "/images/marker-shadow.png",
  });
};

// Call the icon configuration function
iconConfig();

// Feature item with icon
const FeatureItem: React.FC<{
  icon: string;
  title: string;
  value: string;
}> = ({ icon, title, value }) => (
  <div className="flex items-start space-x-3 mb-6">
    <img src={icon} alt={title} className="w-6 h-6 mt-1" />
    <div>
      <h4 className="text-sm font-medium text-gray-500">{title}</h4>
      <p className="text-lg font-semibold text-[#3D3E48]">{value}</p>
    </div>
  </div>
);

// Destination interface with proper types
interface Destination {
  name: string;
  coordinates: LatLngTuple;
  description: string;
  country: string;
}

// Route interface with proper types
interface Route {
  id: number;
  name: string;
  description: string;
  color: string;
  duration: string;
  price: string;
  difficulty: string;
  highlights: string[];
  destinations: Destination[];
}

const BigFiveSafari = () => {
  const [isReady, setIsReady] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState('');
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'details' | 'itinerary' | 'accommodations' | 'map' | 'gallery' | 'inclusions' | 'booking'>('overview');
  const [stickyState, setStickyState] = useState<'top' | 'middle' | 'bottom'>('top');
  const mainContentRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const tourGuideRef = useRef<HTMLDivElement>(null);
  const stickyNavRef = useRef<HTMLDivElement>(null);
  const [isNavSticky, setIsNavSticky] = useState(false);

  // Get icon component based on name
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'car': return <Car className="h-5 w-5" />;
      case 'bed': return <Bed className="h-5 w-5" />;
      case 'users': return <Users className="h-5 w-5" />;
      case 'clock': return <Clock className="h-5 w-5" />;
      case 'calendar': return <CalendarDays className="h-5 w-5" />;
      default: return null;
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch("https://formspree.io/f/xkgjvqwe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tour: "Big Five Safari",
          name,
          email,
          date
        }),
      });
      
      if (response.ok) {
        // Reset form
        setName('');
        setEmail('');
        setDate('');
        // Show success message
        alert("Thank you for your booking request! We'll contact you shortly.");
      } else {
        throw new Error("Error submitting form");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      alert("There was an error submitting your request. Please try again or contact us directly.");
    }
  };

  // Set isReady to true after component mounts to trigger animations
  useEffect(() => {
    setIsReady(true);
  }, []);

  // Content for Big Five Safari
  const tourData = {
    name: "Big Five Safari",
    location: "Nambovic Reserve, Namibia",
    description: "Experience an unforgettable photography safari in Nambovic Reserve, focusing on capturing the iconic Big Five (lion, leopard, elephant, rhino, and buffalo) in their natural habitat.",
    heroImage: "/Tours/bigfivehero.jpeg",
    price: "€2,100",
    duration: "4 days, 3 nights",
    groupSize: "2-6 people",
    details: [
      {
        icon: "car" as const,
        title: "Transportation",
        description: "Safari-equipped 4x4 vehicles with window seats for every photographer"
      },
      {
        icon: "bed" as const,
        title: "Accommodation",
        description: "Premium safari lodges with photography-focused amenities"
      },
      {
        icon: "users" as const,
        title: "Group Size",
        description: "Limited to 6 photographers for personalized attention"
      },
      {
        icon: "clock" as const,
        title: "Duration",
        description: "4 days, 3 nights with multiple photography sessions daily"
      },
      {
        icon: "calendar" as const, 
        title: "Best Time to Visit",
        description: "May-October for clear visibility and wildlife congregation around waterholes"
      }
    ],
    guide: {
      name: "Professor Rion Haraeb",
      imageSrc: "/Tours/rionpropic.jpeg",
      description: "With over 20 years of experience guiding safaris in Namibia since 2000, I'm excited to share my knowledge of the Big Five and help you capture stunning images. As a bilingual guide (English and German), I specialize in wildlife photography and tracking."
    },
    faqs: [
      {
        question: "What photography equipment should I bring?",
        answer: "We recommend bringing a DSLR or mirrorless camera, telephoto lens (at least 200mm, ideally 400mm+), wide-angle lens, tripod, extra batteries, memory cards, and a laptop for editing sessions."
      },
      {
        question: "What is the accommodation like?",
        answer: "You'll stay in premium safari lodges within the reserve. All rooms have private bathrooms, electricity for charging equipment, and areas for reviewing your photos."
      },
      {
        question: "Are meals included in the safari?",
        answer: "Yes, all meals are included. We serve breakfast, lunch, dinner and provide snacks and drinks during game drives."
      },
      {
        question: "What is the chance of seeing all of the Big Five?",
        answer: "While we have a high success rate, wildlife sightings are never guaranteed. The Nambovic Reserve has healthy populations of all Big Five species, and our experienced guides maximize your chances of quality photography opportunities."
      },
      {
        question: "Is this safari suitable for beginner photographers?",
        answer: "Yes, photographers of all skill levels are welcome. Our guides provide personalized instruction based on your experience level, from camera settings to composition techniques."
      }
    ],
    safariImage: "/Tours/BigfiveSafari.jpg",
    included: [
      "Professional bilingual guide (English/German) with wildlife tracking expertise",
      "Transportation in specialized photography safari vehicles",
      "Accommodation in premium safari lodges (3 nights)",
      "All meals and refreshments",
      "Park entrance and conservation fees",
      "Evening photography workshops and editing sessions",
      "Airport transfers from designated meeting points"
    ],
    excluded: [
      "International and domestic flights",
      "Camera equipment and accessories",
      "Travel insurance (mandatory)",
      "Personal expenses and souvenirs",
      "Gratuities for guides and lodge staff",
      "Optional pre/post safari accommodations"
    ],
    mapLocation: {
      lat: -19.7988,
      lng: 17.5368,
      name: "Nambovic Reserve, Namibia"
    },
    itinerary: [
      {
        day: 1,
        title: "Naankuse",
        description: "Arrival at Naankuse Lodge. Introduction to wildlife conservation efforts, evening game drive to spot local wildlife and orientation to the safari experience."
      },
      {
        day: 2,
        title: "Erindi Private Game Reserve",
        description: "Full day exploration of Erindi Private Game Reserve, one of Namibia's premier wildlife destinations. Morning and afternoon game drives to spot the Big Five animals."
      },
      {
        day: 3,
        title: "Otjiwa Lodge",
        description: "Transfer to Otjiwa Lodge, Namibia's oldest game farm. Experience rhino tracking, wildlife photography opportunities, and learn about sustainable game farming practices."
      },
      {
        day: 4,
        title: "Etosha National Park & Return to Windhoek",
        description: "Day trip to Etosha National Park, Namibia's flagship wildlife sanctuary. Explore the eastern section of the park before returning to Windhoek in the evening."
      }
    ],
    accommodation: "Premium safari lodges within the Nambovic Reserve, offering comfortable rooms with ensuite bathrooms, photography workspaces, and views of the African bush. All lodges have electricity for charging camera equipment and Wi-Fi for backing up photos.",
    tourRoute: [
      {
        time: "Day 1, 14:00",
        activity: "Arrival at Naankuse Lodge"
      },
      {
        time: "Day 1, 16:30",
        activity: "Introduction to conservation efforts & evening game drive"
      },
      {
        time: "Day 2, 05:30",
        activity: "Departure to Erindi Private Game Reserve"
      },
      {
        time: "Day 2, 09:00",
        activity: "Morning game drive at Erindi"
      },
      {
        time: "Day 2, 16:00",
        activity: "Afternoon game drive focusing on Big Five"
      },
      {
        time: "Day 3, 07:00",
        activity: "Transfer to Otjiwa Lodge"
      },
      {
        time: "Day 3, 14:00",
        activity: "Rhino tracking experience"
      },
      {
        time: "Day 3, 17:00",
        activity: "Wildlife photography session"
      },
      {
        time: "Day 4, 05:30",
        activity: "Early departure to Etosha National Park"
      },
      {
        time: "Day 4, 10:00",
        activity: "Game viewing in Etosha"
      },
      {
        time: "Day 4, 16:00",
        activity: "Return journey to Windhoek"
      }
    ],
    galleryImages: [
      {
        type: 'video' as const,
        src: "https://res.cloudinary.com/dik2mqn3b/video/upload/q_auto:good,f_auto,c_fill,w_600,h_600/WhatsApp_Video_2025-02-26_at_10.36.59_uxoztb.mp4",
        alt: "Big Five Safari Wildlife"
      },
      {
        type: 'video' as const,
        src: "https://res.cloudinary.com/dik2mqn3b/video/upload/q_auto:good,f_auto,c_fill,w_600,h_600/WhatsApp_Video_2025-02-26_at_10.48.19_ggnezx.mp4",
        alt: "Safari Experience"
      },
      {
        type: 'video' as const,
        src: "https://res.cloudinary.com/dik2mqn3b/video/upload/q_auto:good,f_auto,c_fill,w_600,h_600/WhatsApp_Video_2025-02-26_at_10.41.59_iooile.mp4",
        alt: "Wildlife Encounter"
      },
      {
        type: 'image' as const,
        src: "/Tours/Leopard.jpg",
        alt: "Leopard"
      },
      {
        type: 'image' as const,
        src: "/Tours/Buffaloe.jpg",
        alt: "Buffalo"
      },
      {
        type: 'image' as const,
        src: "/Tours/Lion.jpeg",
        alt: "Lion"
      }
    ]
  };

  // Handle scroll events to control sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!mainContentRef.current || !sidebarRef.current || !tourGuideRef.current || !stickyNavRef.current) return;
      
      const scrollY = window.scrollY;
      const tourGuideOffset = tourGuideRef.current.offsetTop;
      const sidebarHeight = sidebarRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      
      // If the sidebar is taller than the viewport, we can't make it fully sticky
      if (sidebarHeight > windowHeight) {
        if (scrollY > tourGuideOffset - windowHeight) {
          setStickyState('bottom');
        } else {
          setStickyState('top');
        }
      } else {
        // Normal sticky behavior
        if (scrollY > tourGuideOffset - windowHeight) {
          setStickyState('bottom');
        } else if (scrollY > 300) {
          setStickyState('middle');
        } else {
          setStickyState('top');
        }
      }

      if (stickyNavRef.current) {
        const stickyPosition = stickyNavRef.current.offsetTop;
        if (scrollY > stickyPosition) {
          setIsNavSticky(true);
        } else {
          setIsNavSticky(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to section when tab is clicked
  const scrollToSection = (sectionId: string) => {
    setActiveTab(sectionId as 'overview' | 'details' | 'itinerary' | 'accommodations' | 'map' | 'gallery' | 'inclusions' | 'booking');
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -100; // Adjust based on your header height
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div className={`min-h-screen bg-background ${isReady ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}>
      {/* Use our new TourHero component */}
      <TourHero 
        title={tourData.name}
        location={tourData.location}
        description={tourData.description}
        heroImage={tourData.heroImage}
      />

      {/* Trip Details Section - Added top padding to fix overlap issues */}
      <div className="pt-20 md:pt-16 pb-12 md:pb-16 bg-white relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12 relative z-10 animate-fade-up">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-black mb-4">
              Trip Details
            </h2>
            <div className="w-24 h-1 bg-amber-700 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Left column - Trip Details */}
            <div className="animate-fade-up animation-delay-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {tourData.details.map((detail, index) => (
                  <div 
                    key={index} 
                    className="border border-amber-200 rounded-lg p-5 md:p-6 hover:shadow-md transition-all"
                    style={{ animationDelay: `${(index % 2) * 100 + 300}ms` }}
                  >
                    <div className="flex items-center gap-3 mb-2 md:mb-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                        {getIcon(detail.icon)}
                      </div>
                      <h3 className="text-lg md:text-xl font-display font-bold text-black">
                        {detail.title}
                      </h3>
                    </div>
                    <p className="text-sm md:text-base text-gray-800">{detail.description}</p>
                    </div>
                  ))}
                <div className="border border-amber-600 rounded-lg p-5 md:p-6 hover:shadow-md transition-all bg-amber-700 text-white sm:col-span-2 animate-fade-up animation-delay-500">
                  <h3 className="text-lg md:text-xl font-display font-bold mb-2">
                    From {tourData.price} per person
                  </h3>
                  <p className="text-sm md:text-base mb-4">
                    All-inclusive package with luxury accommodations, expert guides, and unforgettable experiences
                  </p>
                  <Link 
                    to="/contact"
                    className="py-3 px-6 bg-white text-amber-900 font-medium rounded-lg hover:bg-amber-50 transition-colors w-full inline-block text-center"
                  >
                    Book Now
                  </Link>
                </div>
              </div>
            </div>
            
            {/* Right column - Safari Highlights */}
            <div className="flex flex-col h-full mt-8 lg:mt-0 animate-fade-up animation-delay-300">
              <div className="relative rounded-lg overflow-hidden h-64 sm:h-80 md:h-96 mb-6">
                <img 
                  src="/Tours/Cheetah.jpg" 
                  alt="Safari highlights"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="h-5 w-5 text-amber-400" />
                    <span className="text-white font-medium">Wildlife Observation</span>
                </div>
                  <h3 className="text-xl md:text-2xl font-display font-bold text-white">
                    Unforgettable Encounters
                  </h3>
                </div>
              </div>
              
              <div className="flex-grow">
                <h3 className="text-xl md:text-2xl font-display font-bold text-black mb-4">
                  Safari Highlights
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 md:gap-4 animate-fade-up animation-delay-400">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="h-4 w-4 md:h-5 md:w-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-black mb-1">Iconic Five Mammals</h4>
                      <p className="text-sm md:text-base text-gray-800">Dedicated focus on photographing elephant, rhino, lion, leopard, and buffalo in their natural habitat.</p>
              </div>
            </div>
                  <div className="flex items-start gap-3 md:gap-4 animate-fade-up animation-delay-500">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="h-4 w-4 md:h-5 md:w-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-black mb-1">Expert Wildlife Tracking</h4>
                      <p className="text-sm md:text-base text-gray-800">Work with specialized trackers to find and photograph elusive predators in optimal conditions.</p>
          </div>
                </div>
                  <div className="flex items-start gap-3 md:gap-4 animate-fade-up animation-delay-600">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <Check className="h-4 w-4 md:h-5 md:w-5 text-amber-700" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-black mb-1">Prime Photography Locations</h4>
                      <p className="text-sm md:text-base text-gray-800">Access to private reserves and photography hides designed for capturing unique wildlife moments.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 animate-fade-up animation-delay-700">
            <TripGallery images={tourData.galleryImages} />
          </div>
        </div>
      </div>

      {/* Content Section - Changed colors from orange/red to black/white */}
      <div className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left column - narrow */}
            <div className="lg:col-span-1 space-y-8 animate-fade-up">
              {/* Booking Form */}
              <div className="bg-white p-8 rounded-2xl shadow-md">
                <h3 className="text-2xl font-display font-bold mb-6 text-black">Book Your Safari</h3>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium block">Full Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/30 focus:border-black"
                      placeholder="Your full name"
                    />
            </div>
                  
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium block">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/30 focus:border-black"
                      placeholder="your.email@example.com"
                    />
          </div>
                  
                  <div className="space-y-2">
                    <label className="text-gray-700 font-medium block">Preferred Tour Date</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full p-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/30 focus:border-black"
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="w-full px-6 py-3 md:py-4 text-base font-extrabold leading-none text-center text-white bg-[rgba(192,91,42,1)] rounded-[40px] shadow-[0px_8px_20px_rgba(192,91,42,0.8)] hover:bg-[rgba(192,91,42,0.9)] transition-all"
                  >
                    Request Booking
                  </button>
                </form>
              </div>
              
              {/* Guide Card - Changed colors */}
              <div className="bg-white p-8 rounded-2xl shadow-md border border-amber-100 animate-fade-up animation-delay-200">
                <div className="flex flex-col items-center text-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden mb-4 ring-4 ring-amber-100">
                    <img
                      src={tourData.guide.imageSrc}
                      alt={tourData.guide.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-black">{tourData.guide.name}</h3>
                  <p className="text-gray-700 mb-4 italic">Expert Safari Guide</p>
                  <p className="text-gray-600 mb-6">"{tourData.guide.description}"</p>
                  <button 
                    onClick={() => setShowGuideModal(true)}
                    className="relative px-4 py-3 bg-white text-amber-900 rounded-lg font-medium group overflow-hidden"
                  >
                    <span className="relative z-10">Meet Your Guide</span>
                    <span className="absolute inset-0 border-2 border-amber-700 rounded-lg"></span>
                    <span className="absolute inset-0 bg-amber-700 scale-0 group-hover:scale-100 transition-transform origin-center duration-300"></span>
                    <span className="absolute inset-0 border-2 border-amber-700 rounded-lg scale-[1.01] opacity-0 group-hover:opacity-100 transition-opacity"></span>
                    <span className="absolute z-[5] inset-0 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">Meet Your Guide</span>
                  </button>
                </div>
              </div>
              
              {/* Q&A Section */}
              <QASection faqs={tourData.faqs} />
            </div>
            
            {/* Right column - wide */}
            <div className="lg:col-span-2 animate-fade-up animation-delay-300">
              {/* Checklist Section */}
              <ChecklistSection 
                imageSrc={tourData.safariImage}
                included={tourData.included}
                excluded={tourData.excluded}
              />

              {/* Tab Content */}
              <TabContent 
                tourRoute={tourData.itinerary.map(item => ({
                  day: `Day ${item.day}: ${item.title}`,
                  description: item.description
                }))}
                itinerary={tourData.tourRoute}
                accommodation={{
                  description: tourData.accommodation,
                  features: [
                    "Luxury safari lodges and tented camps in prime locations",
                    "Private viewing decks with wildlife viewing opportunities",
                    "En-suite facilities with hot showers and comfortable amenities",
                    "Electricity for charging camera equipment",
                    "Communal spaces for photography discussions and image review"
                  ]
                }}
              />
            </div>
          </div>
                </div>
            </div>

      {/* Map Section */}
      <div className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-12 animate-fade-up">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-black mb-4">Explore Our Safari Route</h2>
            <div className="w-24 h-1 bg-amber-700 mx-auto rounded-full mb-8"></div>
          </div>
          
          <div className="h-[500px] border border-amber-200 rounded-lg overflow-hidden shadow-md animate-fade-up animation-delay-200">
            <GoogleMap 
              zoom={7}
              markers={[
                { position: [-22.5609, 17.0658], title: "Windhoek", description: "Starting and ending point of the tour" },
                { position: [-22.4892, 17.0616], title: "Naankuse", description: "Day 1: Wildlife conservation sanctuary" },
                { position: [-21.6431, 16.8665], title: "Erindi Private Game Reserve", description: "Day 2: Premier wildlife viewing destination" },
                { position: [-20.4963, 16.6435], title: "Otjiwa Lodge", description: "Day 3: Namibia's oldest game farm" },
                { position: [-18.8555, 16.3252], title: "Etosha National Park", description: "Day 4: Iconic wildlife sanctuary" }
              ]}
            />
          </div>
        </div>
      </div>
      
      {/* Guide Modal - Updated colors */}
      {showGuideModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowGuideModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-display font-bold text-black">About Your Guide</h2>
              <button onClick={() => setShowGuideModal(false)} className="text-gray-400 hover:text-amber-700">
                ✕
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 mb-6">
              <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0 mx-auto sm:mx-0 ring-4 ring-amber-100">
                <img src={tourData.guide.imageSrc} alt={tourData.guide.name} className="w-full h-full object-cover" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">{tourData.guide.name}</h3>
                <p className="text-gray-700 italic mb-4">Expert Safari Guide</p>
                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-amber-100 text-amber-800 text-sm px-3 py-1 rounded-full">English</span>
                  <span className="bg-amber-100 text-amber-800 text-sm px-3 py-1 rounded-full">German</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <p>Professor Rion Haraeb has been leading safari adventures since 2000, specializing in bilingual tours for international travelers. His extensive knowledge of southern African ecosystems and wildlife makes every safari an educational and unforgettable experience.</p>
              <p>As a professor with deep expertise in the region's biodiversity, Rion offers insights that go beyond the typical safari experience. His personalized approach ensures that each guest receives individual attention and learns about the unique aspects of Namibia's wildlife and culture.</p>
              <p>Rion is committed to sustainable tourism practices and works closely with conservation efforts throughout Namibia. His connections with local communities also provide authentic cultural experiences that many other tours cannot offer.</p>
            </div>
            <div className="mt-8 pt-6 border-t border-amber-100">
              <h4 className="font-bold text-black mb-4">Specialties:</h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                  <span>Wildlife Photography</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                  <span>Bird Identification</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                  <span>Conservation Education</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                  <span>Cultural Interpretation</span>
                </li>
              </ul>
            </div>
            <button 
              onClick={() => setShowGuideModal(false)}
              className="mt-8 w-full px-4 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-all font-medium"
            >
              Close
            </button>
          </div>
      </div>
      )}
      
      {/* Add Footer */}
      <Footer />
    </div>
  );
};

const BigFiveSafariDetail = BigFiveSafari;
export default BigFiveSafariDetail; 